import { fromBech32 } from "@cosmjs/encoding";
import type { V2Coin } from "./action";
import { base64Decode, base64Encode } from "./encoding";
import { protocolFeeCoins } from "./safety";

export type V2FeeAdapter =
  | { fin: Record<string, never> }
  | { ghost_credit: { account_code_checksum: string } }
  | { ghost_vault: { receipt_denom: string } }
  | { bow_xyk: { receipt_denom: string } }
  | { staking: { receipt_denom: string } };

/** Governance metadata only. Product classification belongs to the contract. */
export type V2FeeRules = {
  products: {
    address: string;
    code_checksum: string;
    adapter: V2FeeAdapter;
  }[];
};

export type V2CallFeeQuote = {
  policy_version: number;
  protocol_fee_bps: number;
  taxable_amounts: V2Coin[];
  protocol_fees: V2Coin[];
  relayer_fee_allowed: boolean;
};

const MAX_U128 = (1n << 128n) - 1n;
const NATIVE_DENOM = /^[A-Za-z][A-Za-z0-9/:._-]{2,127}(?![\s\S])/;

const fields = (
  value: unknown,
  expected: readonly string[],
  label: string
): Record<string, unknown> => {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).length !== expected.length ||
    expected.some((key) => !Object.hasOwn(value, key))
  )
    throw new Error(`Invalid ${label}`);
  return value as Record<string, unknown>;
};

const checksum = (value: unknown): string => {
  if (typeof value !== "string")
    throw new Error("Invalid fee product code checksum");
  const decoded = base64Decode(value);
  if (decoded.length !== 32 || base64Encode(decoded) !== value)
    throw new Error("Invalid fee product code checksum");
  return value;
};

export const assertFeeQueryAddress = (
  address: unknown,
  prefix = "thor"
): string => {
  if (typeof address !== "string")
    throw new Error("Invalid private fee query address");
  const decoded = fromBech32(address);
  if (decoded.prefix !== prefix || ![20, 32].includes(decoded.data.length))
    throw new Error("Invalid private fee query address");
  return address;
};

export const parseFeeRules = (value: unknown): V2FeeRules => {
  const rules = fields(value, ["products"], "product fee rules");
  if (!Array.isArray(rules.products) || rules.products.length > 128)
    throw new Error("Invalid fee product registry");
  const seen = new Set<string>();
  const products = rules.products.map((value) => {
    const product = fields(
      value,
      ["address", "code_checksum", "adapter"],
      "fee product"
    );
    const address = assertFeeQueryAddress(product.address);
    if (seen.has(address)) throw new Error("Duplicate fee product");
    seen.add(address);
    const code_checksum = checksum(product.code_checksum);
    const adapter = product.adapter;
    if (!adapter || typeof adapter !== "object" || Array.isArray(adapter))
      throw new Error("Invalid fee product adapter");
    const [kind] = Object.keys(adapter);
    const record = fields(adapter, [kind], "fee product adapter");
    let parsed: V2FeeAdapter;
    if (kind === "fin") {
      fields(record[kind], [], "FIN fee adapter");
      parsed = { fin: {} };
    } else if (kind === "ghost_credit") {
      const detail = fields(
        record[kind],
        ["account_code_checksum"],
        "credit fee adapter"
      );
      parsed = {
        ghost_credit: {
          account_code_checksum: checksum(detail.account_code_checksum),
        },
      };
    } else if (
      kind === "ghost_vault" ||
      kind === "bow_xyk" ||
      kind === "staking"
    ) {
      const detail = fields(record[kind], ["receipt_denom"], "fee adapter");
      if (
        typeof detail.receipt_denom !== "string" ||
        !NATIVE_DENOM.test(detail.receipt_denom)
      )
        throw new Error("Invalid fee product receipt denom");
      parsed = {
        [kind]: { receipt_denom: detail.receipt_denom },
      } as V2FeeAdapter;
    } else {
      // A new adapter must be reviewed before this build can authorize fees.
      throw new Error("Unsupported fee product adapter");
    }
    return { address, code_checksum, adapter: parsed };
  });
  return {
    products: products.sort((a, b) => a.address.localeCompare(b.address)),
  };
};

const quoteCoins = (value: unknown): V2Coin[] => {
  if (!Array.isArray(value) || value.length > 256)
    throw new Error("Invalid private call fee amounts");
  let previous = "";
  return value.map((value) => {
    const coin = fields(value, ["denom", "amount"], "private call fee coin");
    const { denom, amount } = coin;
    if (
      typeof denom !== "string" ||
      !NATIVE_DENOM.test(denom) ||
      denom <= previous ||
      typeof amount !== "string" ||
      amount.length > 39 ||
      !/^[1-9]\d*(?![\s\S])/.test(amount) ||
      BigInt(amount) > MAX_U128
    )
      throw new Error("Invalid private call fee coin");
    previous = denom;
    return { denom, amount };
  });
};

/** Validate the authoritative quote, without guessing a product's fee base. */
export const validateCallFeeQuote = (
  value: unknown,
  config: { fee_policy_version?: number; protocol_fee_bps: number }
): V2CallFeeQuote => {
  const quote = fields(
    value,
    [
      "policy_version",
      "protocol_fee_bps",
      "taxable_amounts",
      "protocol_fees",
      "relayer_fee_allowed",
    ],
    "private call fee quote"
  );
  if (
    typeof quote.policy_version !== "number" ||
    !Number.isSafeInteger(quote.policy_version) ||
    quote.policy_version <= 0 ||
    typeof quote.protocol_fee_bps !== "number" ||
    !Number.isSafeInteger(quote.protocol_fee_bps) ||
    quote.protocol_fee_bps < 0 ||
    quote.protocol_fee_bps > 1_000 ||
    typeof quote.relayer_fee_allowed !== "boolean"
  )
    throw new Error("Invalid private call fee quote policy");
  if (
    quote.policy_version !== config.fee_policy_version ||
    quote.protocol_fee_bps !== config.protocol_fee_bps
  )
    throw Object.assign(
      new Error(
        "Private fees changed. Review the updated fees and confirm again."
      ),
      { code: "PRIVATE_FEE_REVIEW_REQUIRED" }
    );
  const taxable_amounts = quoteCoins(quote.taxable_amounts);
  const protocol_fees = quoteCoins(quote.protocol_fees);
  const expected = protocolFeeCoins(
    new Map(taxable_amounts.map((coin) => [coin.denom, BigInt(coin.amount)])),
    quote.protocol_fee_bps,
    "ceil"
  );
  if (JSON.stringify(protocol_fees) !== JSON.stringify(expected))
    throw new Error("Inconsistent private call fee quote");
  return {
    policy_version: quote.policy_version,
    protocol_fee_bps: quote.protocol_fee_bps,
    taxable_amounts,
    protocol_fees,
    relayer_fee_allowed: quote.relayer_fee_allowed,
  };
};
